import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Persona } from '../data/personas';
import { getChannel, getTitle, moreLikeThis, RowItem } from '../recs/engine';
import { fetchVoiceSuggestion, voiceReplyAudioUrl, VoiceCandidate, VoiceHistoryTurn, VoiceSuggestion } from '../api/justWatch';
import './VoiceAssistant.css';

export const isVoiceSupported = Boolean(navigator.mediaDevices && window.MediaRecorder);

const NEXT_MOVIE_RE = /^\s*(next(\s+movie|\s+one|\s+title)?|play\s+the\s+next(\s+one)?)[\s.,!?]*$/i;
const REFER_TO_LAST_RE = /^\s*(go\s+to|open|play|watch|show\s+me)?\s*(that|it)(\s+one|\s+movie|\s+title)?[\s.,!?]*$/i;
const CANDIDATE_PREVIEW_COUNT = 5;

type TurnStatus = 'idle' | 'recording' | 'thinking';

interface Turn {
  role: 'user' | 'assistant';
  text: string;
  isError?: boolean;
  candidates?: VoiceCandidate[];
  item?: RowItem;
  pending?: boolean;
}

interface Props {
  persona: Persona;
  open: boolean;
  onClose: () => void;
  // Owned by App.tsx and unlocked for playback synchronously within the click that
  // opens this panel — see the SILENT_AUDIO_SRC comment there.
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

function itemFor(titleId: string, reason?: string | null): RowItem | undefined {
  const title = getTitle(titleId);
  if (!title) return undefined;
  return { title, channel: getChannel(title.channel_id), reason: reason ?? undefined };
}

export default function VoiceAssistant({ persona, open, onClose, audioRef }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [turnStatus, setTurnStatus] = useState<TurnStatus>('idle');
  const [messages, setMessages] = useState<Turn[]>([]);
  const [expandedCandidates, setExpandedCandidates] = useState<Record<number, boolean>>({});
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const speechRecognitionRef = useRef<any>(null);
  const liveTranscriptRef = useRef<string>('');
  const autoStartedRef = useRef(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMentionedTitleIdRef = useRef<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const silenceFrameRef = useRef<number | null>(null);
  const lastSoundTimeRef = useRef<number>(0);
  const cancelledRef = useRef(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const OPEN_NAVIGATE_DELAY_MS = 2500;
  const SILENCE_TIMEOUT_MS = 800;
  const SILENCE_VOLUME_THRESHOLD = 0.02;
  // Grace period before the silence watcher can auto-stop — without it, a viewer who
  // takes a beat to start talking gets cut off before saying anything (near-silent
  // clip -> empty transcription from OpenAI, seen repeatedly in prod logs).
  const MIN_RECORDING_MS = 1500;

  const openRef = useRef(open);
  const turnStatusRef = useRef(turnStatus);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  useEffect(() => {
    turnStatusRef.current = turnStatus;
  }, [turnStatus]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, turnStatus]);

  /** Re-arms the mic once the assistant's reply has finished playing, so a
   * voice session keeps going back-and-forth without the viewer having to
   * tap the mic after every turn. Guarded by refs (not the render-time
   * `open`/`turnStatus`) since this fires from an audio/speech event that
   * can land well after the render that scheduled it. */
  const relisten = () => {
    if (openRef.current && turnStatusRef.current === 'idle') startRecording();
  };

  const speakFallback = (text: string, onEnd?: () => void) => {
    if (!('speechSynthesis' in window)) {
      onEnd?.();
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    if (onEnd) utterance.onend = onEnd;
    window.speechSynthesis.speak(utterance);
  };

  const playReply = (suggestion: VoiceSuggestion) => {
    const replyText = suggestion.reply_text;
    if (replyText && audioRef.current) {
      const audioEl = audioRef.current;
      let fellBack = false;
      const fallback = () => {
        if (fellBack) return; // `error` and a rejected play() can both fire for the same failure
        fellBack = true;
        speakFallback(replyText, relisten);
      };
      audioEl.src = voiceReplyAudioUrl(replyText);
      audioEl.onended = relisten;
      audioEl.onerror = fallback; // streamed clip failing mid-fetch (TTS unavailable, network error)
      audioEl.play().catch(fallback);
    } else if (replyText) {
      speakFallback(replyText, relisten);
    } else {
      relisten();
    }
  };

  /** Replaces the pending "listening…" user placeholder (if any) with the real
   * transcript, or drops it if no transcript came back, then appends the
   * given assistant turns — all in one state update. */
  const resolvePending = (transcript: string | null | undefined, assistantTurns: Turn[]) => {
    setMessages((prev) => {
      const withoutPending = prev.filter((m) => !m.pending);
      const userTurn: Turn[] = transcript ? [{ role: 'user', text: transcript }] : [];
      return [...withoutPending, ...userTurn, ...assistantTurns];
    });
  };

  const handleResult = (suggestion: VoiceSuggestion) => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }

    if (!suggestion.available) {
      resolvePending(suggestion.transcript, [
        { role: 'assistant', text: 'Voice assistant unavailable right now.', isError: true },
      ]);
      return;
    }

    if (suggestion.transcript && NEXT_MOVIE_RE.test(suggestion.transcript)) {
      const currentTitleId = location.pathname.match(/^\/title\/([^/]+)/)?.[1];
      const currentTitle = currentTitleId ? getTitle(currentTitleId) : undefined;
      const next = currentTitle
        ? moreLikeThis({ title: currentTitle }).items[0]
        : undefined;
      const replyText = next
        ? `Playing the next one: ${next.title.title_name}.`
        : "You're not watching a title right now, so there's nothing to play next.";
      resolvePending(suggestion.transcript, [{ role: 'assistant', text: replyText, item: next }]);
      speakFallback(replyText, relisten);
      if (next) {
        lastMentionedTitleIdRef.current = next.title.title_id;
        openTimeoutRef.current = setTimeout(() => goToTitle(next.title.title_id), OPEN_NAVIGATE_DELAY_MS);
      }
      return;
    }

    if (suggestion.transcript && REFER_TO_LAST_RE.test(suggestion.transcript)) {
      const referredId = lastMentionedTitleIdRef.current;
      const referredItem = referredId ? itemFor(referredId) : undefined;
      const replyText = referredItem
        ? `Sure — opening ${referredItem.title.title_name}.`
        : "I'm not sure which title you mean — I haven't recommended one yet.";
      resolvePending(suggestion.transcript, [{ role: 'assistant', text: replyText, item: referredItem }]);
      speakFallback(replyText, relisten);
      if (referredItem) {
        openTimeoutRef.current = setTimeout(() => goToTitle(referredItem.title.title_id), OPEN_NAVIGATE_DELAY_MS);
      }
      return;
    }

    const item = suggestion.title_id ? itemFor(suggestion.title_id, suggestion.reply_text) : undefined;
    if (item) lastMentionedTitleIdRef.current = item.title.title_id;

    resolvePending(suggestion.transcript, [
      {
        role: 'assistant',
        text: suggestion.reply_text ?? '',
        candidates: suggestion.candidates ?? undefined,
        item,
      },
    ]);
    playReply(suggestion);

    if (suggestion.action === 'open' && suggestion.title_id) {
      const titleId = suggestion.title_id;
      openTimeoutRef.current = setTimeout(() => goToTitle(titleId), OPEN_NAVIGATE_DELAY_MS);
    }
  };

  const stopSilenceWatch = () => {
    if (silenceFrameRef.current !== null) {
      cancelAnimationFrame(silenceFrameRef.current);
      silenceFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const cancelRecording = () => {
    cancelledRef.current = true;
    mediaRecorderRef.current?.stop();
  };

  const startRecordingTimer = () => {
    setRecordingSeconds(0);
    recordingTimerRef.current = setInterval(() => {
      setRecordingSeconds((s) => s + 1);
    }, 1000);
  };

  const stopRecordingTimer = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  };

  const formatSeconds = (total: number) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const watchForSilence = (stream: MediaStream) => {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextCtor) return;
    const audioContext = new AudioContextCtor();
    audioContextRef.current = audioContext;
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    const data = new Uint8Array(analyser.frequencyBinCount);
    const recordingStartTime = Date.now();
    lastSoundTimeRef.current = recordingStartTime;
    let hasSpoken = false;

    const check = () => {
      analyser.getByteTimeDomainData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i++) {
        const normalized = (data[i] - 128) / 128;
        sumSquares += normalized * normalized;
      }
      const rms = Math.sqrt(sumSquares / data.length);
      const now = Date.now();
      if (rms > SILENCE_VOLUME_THRESHOLD) {
        hasSpoken = true;
        lastSoundTimeRef.current = now;
      } else if (
        // Don't auto-stop until the viewer has actually said something — otherwise
        // someone who's slow to start talking gets cut off with a near-silent clip
        // (empty transcription). Only once they've spoken does the normal
        // silence-after-speech timeout apply.
        hasSpoken &&
        now - recordingStartTime > MIN_RECORDING_MS &&
        now - lastSoundTimeRef.current > SILENCE_TIMEOUT_MS
      ) {
        stopRecording();
        return;
      }
      silenceFrameRef.current = requestAnimationFrame(check);
    };
    silenceFrameRef.current = requestAnimationFrame(check);
  };

  /** Best-effort LIVE captioning via the browser's own SpeechRecognition (when
   * available) so the viewer's bubble can show their actual words the moment
   * recording stops, instead of a generic placeholder — the server-side
   * Whisper transcript (authoritative, works in every browser) still arrives
   * afterwards and overwrites this with the accurate version. */
  const startLiveCaptions = () => {
    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) return;
    liveTranscriptRef.current = '';
    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event: any) => {
      let combined = '';
      for (let i = 0; i < event.results.length; i++) combined += event.results[i][0].transcript;
      liveTranscriptRef.current = combined.trim();
    };
    recognition.onerror = () => {};
    try {
      recognition.start();
      speechRecognitionRef.current = recognition;
    } catch {
      speechRecognitionRef.current = null;
    }
  };

  const stopLiveCaptions = () => {
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
      } catch {
        // ignore
      }
      speechRecognitionRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stopSilenceWatch();
        stopRecordingTimer();
        stopLiveCaptions();
        stream.getTracks().forEach((t) => t.stop());
        if (cancelledRef.current) {
          cancelledRef.current = false;
          setTurnStatus('idle');
          return;
        }
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setTurnStatus('thinking');
        setMessages((prev) => [
          ...prev,
          { role: 'user', text: liveTranscriptRef.current || 'Transcribing your message…', pending: true },
        ]);
        try {
          const history: VoiceHistoryTurn[] = messages.map((m) => ({ role: m.role, text: m.text }));
          const suggestion = await fetchVoiceSuggestion(
            persona.user.user_id,
            blob,
            `recording.${mimeType.includes('mp4') ? 'mp4' : 'webm'}`,
            history
          );
          handleResult(suggestion);
        } catch {
          setMessages((prev) => [
            ...prev.filter((m) => !m.pending),
            { role: 'assistant', text: "Couldn't reach the voice assistant right now.", isError: true },
          ]);
        }
        setTurnStatus('idle');
      };

      cancelledRef.current = false;
      recorder.start();
      setTurnStatus('recording');
      startRecordingTimer();
      watchForSilence(stream);
      startLiveCaptions();
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Microphone access was denied.', isError: true }]);
    }
  };

  useEffect(() => {
    if (!open) {
      autoStartedRef.current = false;
      return;
    }
    if (!autoStartedRef.current && turnStatus === 'idle' && messages.length === 0) {
      autoStartedRef.current = true;
      startRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleMicClick = () => {
    if (turnStatus === 'recording') {
      stopRecording();
    } else if (turnStatus === 'idle') {
      startRecording();
    }
  };

  const handleClose = () => {
    if (turnStatus === 'recording') cancelRecording();
    stopRecordingTimer();
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    setMessages([]);
    setTurnStatus('idle');
    lastMentionedTitleIdRef.current = null;
    onClose();
  };

  const goToTitle = (titleId: string) => {
    handleClose();
    navigate(`/title/${titleId}`);
  };

  const openCandidate = (c: VoiceCandidate) => {
    if (getTitle(c.title_id)) goToTitle(c.title_id);
  };

  if (!open) return null;

  return (
    <div className="voice-overlay">
      {turnStatus !== 'recording' && (
        <div className="voice-overlay__topbar">
          <button className="voice-overlay__close" onClick={handleClose} aria-label="Close voice assistant">
            ✕
          </button>
        </div>
      )}

      <div className="voice-overlay__center">
        {messages.length === 0 ? (
          <>
            <div className={`voice-overlay__mark voice-overlay__mark--${turnStatus}`}>W</div>
            <div className="voice-overlay__intro">
              <p className="voice-overlay__headline">
                {turnStatus === 'recording'
                  ? 'Listening…'
                  : turnStatus === 'thinking'
                  ? 'Thinking…'
                  : 'What are you in the mood for?'}
              </p>
              {turnStatus === 'idle' && (
                <p className="voice-overlay__hint">Try “something funny” or just name a title, like “Thangam”</p>
              )}
            </div>
          </>
        ) : (
          <div className="voice-overlay__chat" ref={chatRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`voice-overlay__bubble voice-overlay__bubble--${m.role}${m.isError ? ' voice-overlay__bubble--error' : ''}${m.pending ? ' voice-overlay__bubble--pending' : ''}`}
              >
                <span className="voice-overlay__bubble-label">{m.role === 'user' ? 'You' : 'mePlay'}</span>
                <p className="voice-overlay__bubble-text">{m.text}</p>
                {m.candidates && (
                  <div
                    className={`voice-overlay__candidates${expandedCandidates[i] ? ' voice-overlay__candidates--expanded' : ''}`}
                  >
                    {(expandedCandidates[i] ? m.candidates : m.candidates.slice(0, CANDIDATE_PREVIEW_COUNT)).map((c) => (
                      <button key={c.title_id} className="voice-overlay__candidate" onClick={() => openCandidate(c)}>
                        <span className="voice-overlay__candidate-title">{c.title_name}</span>
                        <span className="voice-overlay__candidate-meta">
                          {c.genre_primary} · {c.language}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {m.candidates && m.candidates.length > CANDIDATE_PREVIEW_COUNT && !expandedCandidates[i] && (
                  <button
                    className="voice-overlay__candidates-more"
                    onClick={() => setExpandedCandidates((prev) => ({ ...prev, [i]: true }))}
                  >
                    +{m.candidates.length - CANDIDATE_PREVIEW_COUNT} more to go…
                  </button>
                )}
                {m.item && (
                  <button className="voice-overlay__more" onClick={() => goToTitle(m.item!.title.title_id)}>
                    More Info — {m.item.title.title_name}
                  </button>
                )}
              </div>
            ))}
            {turnStatus === 'thinking' && (
              <div className="voice-overlay__bubble voice-overlay__bubble--assistant voice-overlay__bubble--typing">
                <span className="voice-overlay__bubble-label">mePlay</span>
                <p className="voice-overlay__typing-dots">
                  <span />
                  <span />
                  <span />
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {turnStatus === 'recording' ? (
        <div className="voice-recorder">
          <div className="voice-recorder__handle" />
          <div className="voice-recorder__mic-wrap">
            <div className="voice-recorder__mic-ring">
              <span className="voice-recorder__mic-icon">🎙</span>
            </div>
          </div>
          <div className="voice-recorder__timer">{formatSeconds(recordingSeconds)}</div>
          <div className="voice-recorder__waveform" aria-hidden="true">
            {Array.from({ length: 28 }).map((_, i) => (
              <span key={i} className="voice-recorder__bar" style={{ animationDelay: `${(i % 9) * 0.09}s` }} />
            ))}
          </div>
          <div className="voice-recorder__actions">
            <button className="voice-recorder__cancel" onClick={cancelRecording} aria-label="Cancel recording">
              ✕
            </button>
            <button className="voice-recorder__send" onClick={stopRecording} aria-label="Send recording">
              ➤
            </button>
          </div>
        </div>
      ) : (
        <div className="voice-overlay__bottombar">
          <button
            className={`voice-overlay__mic voice-overlay__mic--${turnStatus}`}
            onClick={handleMicClick}
            disabled={turnStatus === 'thinking'}
          >
            🎙
          </button>
        </div>
      )}
    </div>
  );
}
